/**
 * Analytics resource: the one event write, on a client of its own.
 *
 * This is the only file in `client/data` that neither takes a `Datastore` nor
 * reads the provided API client, and it has no read side or cache to
 * invalidate. It builds its own `createApiClient()` lazily instead, for two
 * reasons: an event is recorded from places that hold no `Datastore` at all,
 * and a logging failure must never feed back into more logging, so the
 * transport underneath analytics cannot be one that reports its own errors.
 * Nothing in `createApiClient()` logs or toasts — the client is a bare
 * `RPCLink`, and error handling is each caller's job — so the shared client
 * would work today; holding an instance nobody else does is what keeps a
 * later addition to it from closing that loop.
 */
import type { TrackEventInput } from '@repo/shared/orpc/procedures/analytics';
import { type ApiClient, createApiClient } from 'system/orpc';

let analyticsApiClient: ApiClient | null = null;

function getAnalyticsApiClient(): ApiClient {
    analyticsApiClient ??= createApiClient();
    return analyticsApiClient;
}

/**
 * Records one event. Rejects on a transport or validation failure, which the
 * caller is expected to swallow rather than report — see the file header.
 */
export async function trackEventAsync(event: TrackEventInput): Promise<void> {
    await getAnalyticsApiClient().analytics.trackEvent(event);
}
